import { Dispatch, SetStateAction, useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { TabContext, TabPanel } from "@mui/lab";
import { Alert, Box, Stack, Typography as T, Tab, Tabs, tabsClasses } from "@mui/material";
import ConceptTypeBadge from "catalog/components/ConceptTypeBadge";
import {
  ConceptGroup as ConceptGroupType,
  TypeCategorizedConceptGroup,
  useConceptGroupsList,
} from "catalog/hooks/useConceptGroupsList";
import useCurrentConceptGroup from "catalog/hooks/useCurrentConceptGroup";
import ConceptEntity, { DISPLAY_CONCEPT_ENTITY } from "catalog/types/enums/conceptEntity";
import pluralize from "pluralize";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import EmptyState from "~/components/EmptyState";
import FlatCard from "~/components/FlatCard";
import ListCard, { LinkListItem } from "~/components/ListCard";
import TextOverflow from "~/components/TextOverflow";

const numFormatter = Intl.NumberFormat("en", { notation: "compact" });

const CategoryGroupButtons = ({
  groups,
  selectedGroup,
  setSelectedGroup,
}: {
  groups: TypeCategorizedConceptGroup[];
  selectedGroup: ConceptEntity;
  setSelectedGroup: Dispatch<SetStateAction<ConceptEntity>>;
}) => {
  // There must be two or more types of groups (e.g. Derived, Question) for
  // these buttons to show up. Otherwise, they are hidden and we will always
  // show the only group available.
  if (groups.length >= 2) {
    return (
      <Tabs
        sx={{
          mb: 2,
          [`&.${tabsClasses.root}`]: { border: "none" },
          [`.${tabsClasses.flexContainer}`]: {
            borderBottom: "1px solid",
            borderColor: "common.toolbarBorder",
          },
        }}
        value={selectedGroup}
        onChange={(_, value) => setSelectedGroup(value)}
      >
        {groups.map(({ type, conceptGroups }) => {
          const label = `${pluralize(DISPLAY_CONCEPT_ENTITY[type!])} (${
            conceptGroups?.length ?? 0
          })`;
          return <Tab key={type} label={label} value={type} />;
        })}
      </Tabs>
    );
  }
  return null;
};

const CategoryGroupTabs = ({
  groupId,
  loading,
  groups,
}: {
  groupId: string;
  loading: boolean;
  groups: TypeCategorizedConceptGroup[];
}) => {
  const [selectedGroup, setSelectedGroup] = useState(ConceptEntity.DERIVED_GROUP);

  // Reset activeGroup to the default value when groupId changes.
  // This way, we always default the user to Derived Group.
  useEffect(() => {
    setSelectedGroup(ConceptEntity.DERIVED_GROUP);
  }, [groupId]);

  // Filter out empty concept groups
  const filteredCategoryGroups = groups.filter(
    ({ type, conceptGroups }) => type && conceptGroups?.length
  );

  return (
    <TabContext value={selectedGroup}>
      <CategoryGroupButtons
        groups={filteredCategoryGroups}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
      />
      {filteredCategoryGroups.map((group) => (
        <TabPanel sx={{ padding: 0 }} key={group.type} value={group.type!}>
          <ListCard loading={loading}>
            {group?.conceptGroups?.map(({ id, isLeafNode, description, ...rest }) => {
              const path = isLeafNode
                ? `/data-browser/dashboards/concepts/${id}/details`
                : `/data-browser/dashboards/concepts/${id}`;
              return (
                <ConceptGroup key={id} id={id} path={path} description={description} {...rest} />
              );
            })}
          </ListCard>
        </TabPanel>
      ))}
    </TabContext>
  );
};

const ConceptGroup = ({
  path,
  name,
  type,
  category,
  description,
  counts,
  isLeafNode,
}: ConceptGroupType & { path: string }) => {
  const [query] = useQueryParam("q", withDefault(StringParam, ""));
  const addSearchToUrl = !!query && !isLeafNode;
  const participantCount = counts.find(({ type }) => type === ConceptEntity.PARTICIPANT)?.count;
  const conceptTypeLabel = DISPLAY_CONCEPT_ENTITY[type];
  const formattedCounts = counts
    .filter(({ type }) => type !== ConceptEntity.PARTICIPANT)
    .map(({ count, type }) => {
      const name = DISPLAY_CONCEPT_ENTITY[type];
      if (name) {
        return `${numFormatter.format(count)} ${pluralize(name, count)}`;
      }
    })
    .filter(Boolean)
    .join(" · ");

  return (
    <LinkListItem
      to={{ pathname: path, search: addSearchToUrl ? `q=${query}` : undefined }}
      navOptions={{ relative: "path" }}
      rootStyles={{ px: 1 }}
    >
      <Stack gap={0.25} data-testid="ConceptGroup" overflow="hidden">
        <T variant="body1" fontWeight={500}>
          {name}
        </T>
        <Box
          color="primary.dark"
          display="flex"
          alignItems="center"
          flexWrap="nowrap"
          gap={1}
          width="100%"
          fontSize={14}
          sx={{ opacity: 0.67 }}
        >
          {formattedCounts && (
            <Box sx={{ whiteSpace: "nowrap", textTransform: "lowercase" }}>{formattedCounts}</Box>
          )}
          {formattedCounts && description && <Box> · </Box>}
          <TextOverflow
            variant="inherit"
            sx={{
              "&::first-letter": {
                textTransform: "capitalize",
              },
            }}
          >
            {description}
          </TextOverflow>
        </Box>
      </Stack>
      <Box display="flex" alignItems="center" justifyContent="end" gap={1.5} fontSize={18} flex={1}>
        <ConceptTypeBadge label={conceptTypeLabel} category={category} />
        {participantCount !== undefined && (
          <T
            variant="body1"
            fontWeight={500}
            sx={{ opacity: 0.5, minWidth: 55, textAlign: "right" }}
          >
            {numFormatter.format(participantCount)}
          </T>
        )}
        <ArrowForwardIos sx={{ fontSize: 14, opacity: 0.4 }} />
      </Box>
    </LinkListItem>
  );
};

const ConceptGroupList = () => {
  const { groupId = "" } = useParams();
  const { typeCategorizedGroups, loading, error } = useConceptGroupsList(groupId);
  const groupInfo = useCurrentConceptGroup();

  // For Cancer list, we also render the total participant count header.
  const showParticipantTotalsHeader = useMemo(() => {
    if (!typeCategorizedGroups.length) {
      return false;
    }
    const { conceptGroups } = typeCategorizedGroups[0];
    return conceptGroups?.some(
      (d) => d.counts.find(({ type }) => type === ConceptEntity.PARTICIPANT)?.count
    );
  }, [typeCategorizedGroups]);

  if (error) {
    return (
      <Alert severity="error" variant="outlined">
        Unable to load concepts
      </Alert>
    );
  }

  if (!loading && !typeCategorizedGroups?.length) {
    return (
      <FlatCard>
        <EmptyState />
      </FlatCard>
    );
  }

  return (
    <Box>
      {groupInfo?.description && (
        <Box
          component="span"
          sx={{
            display: "inline-block",
            fontSize: 16,
            opacity: 0.67,
            lineHeight: 1.4,
            mb: 2,
            "&::first-letter": { textTransform: "capitalize" },
          }}
        >
          {groupInfo?.description}
        </Box>
      )}
      {showParticipantTotalsHeader && (
        <Box
          sx={{
            fontSize: 14,
            fontWeight: 500,
            textAlign: "right",
            pr: 4,
            mb: 1.5,
            opacity: 0.5,
          }}
        >
          Total Participants
        </Box>
      )}

      {/* If there's only one category group, simply render it */}
      {typeCategorizedGroups.length === 1 && (
        <ListCard loading={loading}>
          {typeCategorizedGroups[0].conceptGroups?.map(({ id, isLeafNode, ...rest }) => {
            const path = isLeafNode
              ? `/data-browser/dashboards/concepts/${id}/details`
              : `/data-browser/dashboards/concepts/${id}`;
            return <ConceptGroup key={id} id={id} path={path} {...rest} />;
          })}
        </ListCard>
      )}

      {/* If multiple category groups exist, render each in a tab */}
      {typeCategorizedGroups.length > 1 && (
        <CategoryGroupTabs groupId={groupId} loading={loading} groups={typeCategorizedGroups} />
      )}
    </Box>
  );
};

export default ConceptGroupList;
